import { useState } from "react";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { Check, ChevronsUpDown, Filter, X } from "lucide-react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useGuildRoute } from "@/hooks/use-guild-route";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  TicketsList,
  TicketStats,
  TicketSettings,
  TicketCategories,
  TicketDeployPanel,
} from "@/components/tickets";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { UpgradeBanner } from "@/components/subscription/upgrade-banner";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  ticketStatsQueryOptions,
  ticketSettingsQueryOptions,
  useTicketCategories,
  guildChannelsQueryOptions,
  guildRolesQueryOptions,
  guildEmojisQueryOptions,
} from "@/lib/api";
import { useGuildSubscriptionFeatures } from "@/hooks/use-guild-subscription-features";
import { useGuildPermissions } from "@/hooks/use-guild-permissions";
import { cn } from "@/lib/utils";
import { EmojiDisplay } from "@/components/discord/emoji-utils";

export type TicketsSearch = {
  status?: string;
  subStatus?: string;
  priority?: string;
  category?: string;
  type?: string;
  tag?: string;
  search?: string;
  assignee?: string;
  creator?: string;
  page?: number;
  tab?: string;
};

const TICKETS_SEARCH_DEFAULTS: Required<TicketsSearch> = {
  status: "active",
  subStatus: "all",
  priority: "all",
  category: "all",
  type: "all",
  tag: "all",
  search: "",
  assignee: "",
  creator: "",
  page: 0,
  tab: "tickets",
};

export function validateTicketsSearch(
  search: Record<string, unknown>,
  defaults: TicketsSearch = TICKETS_SEARCH_DEFAULTS
): TicketsSearch {
  return {
    status: typeof search.status === "string" ? search.status : defaults.status,
    subStatus:
      typeof search.subStatus === "string"
        ? search.subStatus
        : defaults.subStatus,
    priority:
      typeof search.priority === "string" ? search.priority : defaults.priority,
    category:
      typeof search.category === "string" ? search.category : defaults.category,
    type: typeof search.type === "string" ? search.type : defaults.type,
    tag: typeof search.tag === "string" ? search.tag : defaults.tag,
    search: typeof search.search === "string" ? search.search : defaults.search,
    assignee:
      typeof search.assignee === "string" ? search.assignee : defaults.assignee,
    creator:
      typeof search.creator === "string" ? search.creator : defaults.creator,
    page: typeof search.page === "number" ? search.page : defaults.page,
    tab: typeof search.tab === "string" ? search.tab : defaults.tab,
  };
}

export const Route = createFileRoute(
  "/dashboard/guilds/$applicationGuildId/tickets/"
)({
  component: TicketsPage,
  validateSearch: (search: Record<string, unknown>) =>
    validateTicketsSearch(search),
});

function TicketsPage() {
  usePageTitle("Tickets");
  const { applicationGuildId, isLocked } = useGuildRoute();
  const search = useSearch({ from: Route.fullPath });
  const navigate = useNavigate({ from: Route.fullPath });
  const { isFreePlan, features } = useGuildSubscriptionFeatures();
  const { canEdit } = useGuildPermissions();
  const isReadOnly = isLocked || !canEdit("tickets");

  const setActiveTab = (tab: string) => {
    navigate({ search: (prev) => ({ ...prev, tab }), replace: true });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-balance text-3xl font-bold">Support Tickets</h1>
        <p className="text-muted-foreground text-pretty">
          Manage support tickets and configure the ticket system
        </p>
      </div>

      {isFreePlan &&
        features &&
        (!features.ticketClaiming ||
          !features.ticketTranscripts ||
          !features.ticketAI) && (
          <UpgradeBanner
            feature="Unlock Pro Ticket Features"
            description="Upgrade to Pro to enable ticket claiming, transcript generation, AI auto-responses, and more."
          />
        )}

      <Tabs
        value={search.tab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList>
          <TabsTrigger value="tickets">Tickets</TabsTrigger>
          <TabsTrigger value="stats">Statistics</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="tickets" className="space-y-6">
          <TicketsList applicationGuildId={applicationGuildId} />
        </TabsContent>

        <TabsContent value="stats" className="space-y-6">
          {search.tab === "stats" && (
            <StatsTab applicationGuildId={applicationGuildId} />
          )}
        </TabsContent>

        <TabsContent value="categories">
          <TicketCategories
            applicationGuildId={applicationGuildId}
            isLocked={isReadOnly}
          />
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          {search.tab === "settings" && (
            <SettingsTab
              applicationGuildId={applicationGuildId}
              isReadOnly={isReadOnly}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatsTab({ applicationGuildId }: { applicationGuildId: string }) {
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const {
    data: statsData,
    isPending,
    isPlaceholderData,
  } = useQuery(
    ticketStatsQueryOptions(
      applicationGuildId,
      selectedCategoryIds.length > 0 ? selectedCategoryIds : undefined
    )
  );
  const { data: categoriesData } = useTicketCategories(applicationGuildId);

  const stats = statsData?.data;
  const categories = categoriesData?.data ?? [];

  const toggleCategory = (id: number) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const statsSkeleton = (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-card space-y-3 rounded-xl border p-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="size-8 rounded-lg" />
            </div>
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-card rounded-xl border p-6">
            <Skeleton className="mb-4 h-5 w-28" />
            <Skeleton className="h-40 w-full" />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Filter className="size-4" />
              Categories
              {selectedCategoryIds.length > 0 && (
                <Badge variant="secondary" className="tabular-nums">
                  {selectedCategoryIds.length}
                </Badge>
              )}
              <ChevronsUpDown className="size-3 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <Command>
              <CommandInput placeholder="Search categories..." />
              <CommandList>
                <CommandEmpty>No categories found.</CommandEmpty>
                <CommandGroup>
                  {categories.map((category) => (
                    <CommandItem
                      key={category.id}
                      value={category.name}
                      onSelect={() => toggleCategory(category.id)}
                    >
                      <Check
                        className={cn(
                          "mr-2 size-4",
                          selectedCategoryIds.includes(category.id)
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                      {category.emoji && (
                        <EmojiDisplay
                          emoji={category.emoji}
                          className="mr-1.5 size-4"
                        />
                      )}
                      {category.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {selectedCategoryIds.length > 0 && (
          <>
            {selectedCategoryIds.map((id) => {
              const cat = categories.find((c) => c.id === id);
              if (!cat) return null;
              return (
                <Badge
                  key={id}
                  variant="secondary"
                  className="cursor-pointer gap-1"
                  onClick={() => toggleCategory(id)}
                >
                  {cat.emoji && (
                    <EmojiDisplay emoji={cat.emoji} className="size-4" />
                  )}
                  {cat.name}
                  <X className="size-3" />
                </Badge>
              );
            })}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setSelectedCategoryIds([])}
            >
              Clear all
            </Button>
          </>
        )}
      </div>

      {!stats && isPending ? (
        statsSkeleton
      ) : stats ? (
        <TicketStats
          stats={stats}
          isLoading={isPending && !isPlaceholderData}
        />
      ) : null}
    </div>
  );
}

function SettingsTab({
  applicationGuildId,
  isReadOnly,
}: {
  applicationGuildId: string;
  isReadOnly: boolean;
}) {
  const [
    { data: settingsData },
    { data: channelsData },
    { data: rolesData },
    { data: emojisData },
  ] = useQueries({
    queries: [
      ticketSettingsQueryOptions(applicationGuildId),
      guildChannelsQueryOptions(applicationGuildId),
      guildRolesQueryOptions(applicationGuildId),
      guildEmojisQueryOptions(applicationGuildId),
    ],
  });

  const settings = settingsData?.data;
  const channels = channelsData?.data;
  const roles = rolesData?.data;
  const emojis = emojisData?.data;

  return (
    <>
      <TicketDeployPanel
        applicationGuildId={applicationGuildId}
        settings={settings || null}
        channels={channels || null}
        isLocked={isReadOnly}
      />
      <TicketSettings
        settings={settings || null}
        channels={channels || null}
        roles={roles || null}
        emojis={emojis || null}
        isLocked={isReadOnly}
      />
    </>
  );
}
